import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout, Button, Input, Card, Tag, Space, Typography, Tooltip,
  Upload, Spin, message, Modal, List, Avatar, Empty, Divider,
  Switch, Badge, Alert, Row, Col, Popconfirm,
} from 'antd';
import {
  PlusOutlined, SendOutlined, DeleteOutlined, EditOutlined,
  FacebookOutlined, UploadOutlined, LinkOutlined, PictureOutlined,
  RobotOutlined, UserOutlined, CopyOutlined, CheckOutlined,
  GlobalOutlined, HeartOutlined, CommentOutlined, ShareAltOutlined,
  ThunderboltOutlined, ReloadOutlined, EyeOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const API = '/api/v1/fb-ads';
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`,
  'Content-Type': 'application/json',
});

const api = {
  list: () => fetch(`${API}/`, { headers: authHeader() }).then(r => r.json()),
  create: (body) => fetch(`${API}/`, { method: 'POST', headers: authHeader(), body: JSON.stringify(body) }).then(r => r.json()),
  get: (id) => fetch(`${API}/${id}`, { headers: authHeader() }).then(r => r.json()),
  update: (id, body) => fetch(`${API}/${id}`, { method: 'PATCH', headers: authHeader(), body: JSON.stringify(body) }).then(r => r.json()),
  delete: (id) => fetch(`${API}/${id}`, { method: 'DELETE', headers: authHeader() }).then(r => r.json()),
  chat: (id, msg) => fetch(`${API}/${id}/chat`, { method: 'POST', headers: authHeader(), body: JSON.stringify({ message: msg }) }).then(r => r.json()),
  publish: (id) => fetch(`${API}/${id}/publish`, { method: 'POST', headers: authHeader() }).then(r => r.json()),
  imageUrl: (id) => `${API}/${id}/image`,
};

// ── Facebook Ad Preview ────────────────────────────────────────────────────────
function FbAdPreview({ ad, onEdit }) {
  const { t } = useTranslation();
  const img = ad?.image_url || (ad?.id ? api.imageUrl(ad.id) : null);
  const hasImage = img && (ad?.image_url || ad?.image_path);

  return (
    <div style={{
      maxWidth: 500, margin: '0 auto',
      background: '#fff', borderRadius: 8,
      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
      fontFamily: '-apple-system,system-ui,sans-serif',
      overflow: 'hidden',
    }}>
      {/* ── FB Post Header ── */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          size={40}
          style={{ background: '#1877f2', fontSize: 18, fontWeight: 700, flexShrink: 0 }}
        >C</Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#050505' }}>
            {ad?.page_name || 'Comboware AI'}
          </div>
          <div style={{ fontSize: 12, color: '#65676b', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>Sponsored</span>
            <GlobalOutlined style={{ fontSize: 10 }} />
          </div>
        </div>
        <Button type="primary" size="small" style={{ background: '#1877f2', border: 'none', borderRadius: 6 }}>
          + Follow
        </Button>
      </div>

      {/* ── Ad Body Text (上) ── */}
      <div style={{ padding: '0 16px 10px' }}>
        {ad?.title && (
          <div style={{ fontWeight: 600, fontSize: 15, color: '#050505', marginBottom: 6, lineHeight: 1.4 }}>
            {ad.title}
          </div>
        )}
        {ad?.body ? (
          <div style={{ fontSize: 14, color: '#050505', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {ad.body.length > 200 ? ad.body.slice(0, 200) + `… ${t('fbAd.readMore','Read more')}` : ad.body}
          </div>
        ) : (
          <div style={{ fontSize: 14, color: '#ccc', fontStyle: 'italic' }}>{t('fbAd.bodyPlaceholderPreview','Ad body text will appear here...')}</div>
        )}
      </div>

      {/* ── Ad Image (中) ── */}
      <div
        style={{
          width: '100%', minHeight: 260, background: '#f0f2f5',
          position: 'relative', cursor: 'pointer', overflow: 'hidden',
        }}
        onClick={() => onEdit?.('image')}
      >
        {hasImage ? (
          <img
            src={img}
            alt="ad"
            style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 360 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            minHeight: 260, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12,
            color: '#65676b', border: '2px dashed #d9d9d9',
          }}>
            <PictureOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
            <Text type="secondary">{t('fbAd.clickUpload','Click to upload image')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.imgRecommend','Recommended: 1200×628px')}</Text>
          </div>
        )}
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: 'rgba(0,0,0,0.5)', borderRadius: 4,
          padding: '2px 8px', color: '#fff', fontSize: 12, cursor: 'pointer',
        }}
          onClick={e => { e.stopPropagation(); onEdit?.('image'); }}
        >
          <EditOutlined /> {t('fbAd.changeImage','Change Image')}
        </div>
      </div>

      {/* ── CTA Link (下) ── */}
      <div style={{
        background: '#f0f2f5', padding: '10px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: '1px solid #e4e6ea',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#65676b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {ad?.cta_url ? new URL(ad.cta_url.startsWith('http') ? ad.cta_url : 'https://' + ad.cta_url).hostname.replace('www.', '') : 'comboware.ai'}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#050505', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ad?.title || t('fbAd.adTitle','Ad Title')}
          </div>
        </div>
        <Button
          type="primary"
          size="small"
          style={{ background: '#1877f2', border: 'none', borderRadius: 6, marginLeft: 12, flexShrink: 0 }}
          onClick={() => onEdit?.('cta')}
        >
          {ad?.cta_label || t('fbAd.learnMore','Learn More')}
        </Button>
      </div>

      {/* ── Reactions bar ── */}
      <div style={{
        padding: '8px 16px', display: 'flex', gap: 8, borderTop: '1px solid #e4e6ea',
      }}>
        {[
          { icon: <HeartOutlined />, label: t('fbAd.like','Like') },
          { icon: <CommentOutlined />, label: t('fbAd.comment','Comment') },
          { icon: <ShareAltOutlined />, label: t('fbAd.share','Share') },
        ].map(({ icon, label }) => (
          <Button key={label} type="text" size="small" icon={icon}
            style={{ color: '#65676b', fontWeight: 600 }}>
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function FbAdCreatorPage() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('manual');   // 'manual' | 'ai'
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editField, setEditField] = useState(null);  // which field is being edited
  const msgEndRef = useRef(null);

  const reload = useCallback(async () => {
    const data = await api.list();
    setList(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selected?.messages]);

  const selectAd = async (id) => {
    const data = await api.get(id);
    setSelected(data);
  };

  const createNew = async () => {
    const ad = await api.create({ title: '', body: '', page_name: 'Comboware AI' });
    if (ad?.id) {
      await reload();
      setSelected(ad);
      setMode('manual');
    }
  };

  const handleUpdate = async (field, value) => {
    if (!selected) return;
    const updated = await api.update(selected.id, { [field]: value });
    setSelected(updated);
    reload();
  };

  const handleImageUpload = async ({ file }) => {
    if (!selected) return;
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API}/${selected.id}/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}` },
      body: form,
    });
    const data = await res.json();
    if (data.ok) {
      // Reload to get fresh data
      const fresh = await api.get(selected.id);
      setSelected({ ...fresh, _imgKey: Date.now() });
      reload();
      message.success(t('fbAd.uploadSuccess','Image uploaded'));
    } else {
      message.error(data.detail || t('fbAd.uploadFailed','Upload failed'));
    }
    return false;
  };

  const handleChat = async () => {
    if (!chatInput.trim() || !selected) return;
    const msg = chatInput.trim();
    setChatInput('');
    setSending(true);
    // Optimistic update
    setSelected(prev => ({
      ...prev,
      messages: [...(prev.messages || []), { role: 'user', content: msg }],
    }));
    try {
      const res = await api.chat(selected.id, msg);
      setSelected(res.ad);
      reload();
    } finally {
      setSending(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.publish(selected.id);
      if (res.ok) {
        message.success(`${t('fbAd.publishedFb','Published to Facebook!')} Post ID: ${res.post_id}`);
        const fresh = await api.get(selected.id);
        setSelected(fresh);
        reload();
      } else {
        message.error(res.detail || t('fbAd.publishFailed','Publish failed'));
      }
    } catch (e) {
      message.error(t('fbAd.publishFailed','Publish failed'));
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    Modal.confirm({
      title: t('fbAd.confirmDelete','Delete this ad draft?'),
      okText: t('fbAd.delete','Delete'), cancelText: t('fbAd.cancel','Cancel'), okButtonProps: { danger: true },
      onOk: async () => {
        await api.delete(id);
        if (selected?.id === id) setSelected(null);
        await reload();
      },
    });
  };

  const imgSrc = selected
    ? (selected.image_url || (selected.image_path ? `${api.imageUrl(selected.id)}?t=${selected._imgKey || 0}` : null))
    : null;

  // Quick prompts
  const QUICK_PROMPTS = [
    t('fbAd.prompt1','Create a Comboware AI brand ad highlighting 7 AI product lines, targeting business owners'),
    t('fbAd.prompt2','Create an AlayaEss AI staffing ad: 24/7 availability, 1/10 the cost of traditional staff'),
    t('fbAd.prompt3','Create a ComboPilot AI platform ad for IT managers: fully automated from requirements to deployment'),
    t('fbAd.prompt4','Create a MarCom AI marketing platform ad: auto-generated content, omnichannel publishing, higher ROI'),
  ];

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#f0f2f5' }}>
      {/* ── Left: Ad list ── */}
      <Sider width={240} style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'auto', padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Text strong style={{ fontSize: 14 }}>{t('fbAd.draftList','Ad Drafts')}</Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={createNew}>{t('fbAd.newAd','New')}</Button>
        </div>
        {list.length === 0 ? (
          <Empty description={t('fbAd.noAds','No ads yet')} style={{ marginTop: 40 }} />
        ) : list.map(ad => (
          <div
            key={ad.id}
            onClick={() => selectAd(ad.id)}
            style={{
              padding: '9px 10px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
              background: selected?.id === ad.id ? '#e6f4ff' : '#fafafa',
              border: selected?.id === ad.id ? '1px solid #1890ff' : '1px solid #f0f0f0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Text ellipsis style={{ maxWidth: 165, fontSize: 13, fontWeight: selected?.id === ad.id ? 600 : 400 }}>
                {ad.title || <span style={{ color: '#bbb' }}>{t('fbAd.unnamed','(Unnamed)')}</span>}
              </Text>
              <Popconfirm title={t('fbAd.confirmDelete','Delete this ad draft?')} onConfirm={e => handleDelete(ad.id)} okText={t('fbAd.delete','Delete')} cancelText={t('fbAd.cancel','Cancel')} okButtonProps={{ danger: true }}>
                <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={e => e.stopPropagation()} />
              </Popconfirm>
            </div>
            <div style={{ marginTop: 4 }}>
              <Tag color={ad.status === 'published' ? 'success' : 'default'} style={{ fontSize: 10, padding: '0 4px' }}>
                {ad.status === 'published' ? t('fbAd.published','Published') : t('fbAd.draft','Draft')}
              </Tag>
              {ad.messages?.length > 0 && (
                <Tag icon={<RobotOutlined />} color="purple" style={{ fontSize: 10, padding: '0 4px' }}>
                  AI {Math.floor(ad.messages.length / 2)} {t('fbAd.rounds','rounds')}
                </Tag>
              )}
            </div>
          </div>
        ))}
      </Sider>

      {/* ── Right: Editor + Preview ── */}
      <Content style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
            <FacebookOutlined style={{ fontSize: 64, color: '#1877f2' }} />
            <Title level={4} type="secondary">{t('fbAd.pageTitle','FB Ad Creator')}</Title>
            <Text type="secondary">{t('fbAd.pageDesc','Click New to start. Supports manual editing and AI chat modes.')}</Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={createNew} size="large">{t('fbAd.newAd','New Ad')}</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

            {/* ── Editor Panel (left) ── */}
            <div style={{ flex: '0 0 420px', display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #f0f0f0' }}>
              {/* Header + mode switch */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <FacebookOutlined style={{ color: '#1877f2', fontSize: 18 }} />
                    <Text strong style={{ fontSize: 14 }}>{t('fbAd.editor','Ad Editor')}</Text>
                    {selected.status === 'published' && <Tag color="success">{t('fbAd.published','Published')}</Tag>}
                  </Space>
                  <Space>
                    <Tag color={mode === 'ai' ? 'purple' : 'blue'}>{mode === 'ai' ? `🤖 ${t('fbAd.aiMode','AI Mode')}` : `✏️ ${t('fbAd.manualMode','Manual Mode')}`}</Tag>
                    <Switch
                      checkedChildren={<RobotOutlined />}
                      unCheckedChildren={<EditOutlined />}
                      checked={mode === 'ai'}
                      onChange={v => setMode(v ? 'ai' : 'manual')}
                    />
                  </Space>
                </div>
              </div>

              {/* ── MANUAL MODE ── */}
              {mode === 'manual' && (
                <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size={14}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.pageName','Page Name')}</Text>
                      <Input
                        value={selected.page_name || ''}
                        onChange={e => setSelected(p => ({ ...p, page_name: e.target.value }))}
                        onBlur={e => handleUpdate('page_name', e.target.value)}
                        prefix={<FacebookOutlined style={{ color: '#1877f2' }} />}
                        placeholder="Comboware AI"
                      />
                    </div>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.adTitleLabel','Ad Title')} <Text type="danger">*</Text></Text>
                      <Input
                        value={selected.title || ''}
                        onChange={e => setSelected(p => ({ ...p, title: e.target.value }))}
                        onBlur={e => handleUpdate('title', e.target.value)}
                        placeholder={t('fbAd.titlePlaceholder','Compelling ad title (under 40 chars)')}
                        showCount maxLength={40}
                      />
                    </div>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.adBody','Ad Copy')} <Text type="danger">*</Text></Text>
                      <TextArea
                        value={selected.body || ''}
                        onChange={e => setSelected(p => ({ ...p, body: e.target.value }))}
                        onBlur={e => handleUpdate('body', e.target.value)}
                        placeholder={t('fbAd.bodyPlaceholder','Ad copy using AIDA structure. Include emojis and line breaks, 150-300 words.')}
                        autoSize={{ minRows: 5, maxRows: 10 }}
                        showCount maxLength={600}
                      />
                    </div>

                    <Divider style={{ margin: '4px 0' }}>{t('fbAd.adImage','Ad Image')}</Divider>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.uploadImg','Upload image (recommended 1200×628px)')}</Text>
                      <Upload.Dragger
                        beforeUpload={() => false}
                        customRequest={handleImageUpload}
                        showUploadList={false}
                        accept="image/*"
                        style={{ marginTop: 4 }}
                      >
                        {imgSrc ? (
                          <img src={imgSrc} alt="" style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                          <div style={{ padding: '12px 0' }}>
                            <PictureOutlined style={{ fontSize: 28, color: '#bfbfbf' }} />
                            <div style={{ fontSize: 13, color: '#999', marginTop: 8 }}>{t('fbAd.dragOrClick','Drag or click to upload')}</div>
                          </div>
                        )}
                      </Upload.Dragger>
                    </div>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.orUrl','Or enter image URL')}</Text>
                      <Input
                        value={selected.image_url || ''}
                        onChange={e => setSelected(p => ({ ...p, image_url: e.target.value }))}
                        onBlur={e => handleUpdate('image_url', e.target.value || null)}
                        placeholder="https://example.com/image.jpg"
                        prefix={<LinkOutlined />}
                      />
                    </div>

                    <Divider style={{ margin: '4px 0' }}>{t('fbAd.ctaSection','Link Button')}</Divider>

                    <Row gutter={10}>
                      <Col span={16}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.ctaUrl','CTA Link')}</Text>
                        <Input
                          value={selected.cta_url || ''}
                          onChange={e => setSelected(p => ({ ...p, cta_url: e.target.value }))}
                          onBlur={e => handleUpdate('cta_url', e.target.value)}
                          placeholder="https://comboware.ai"
                          prefix={<GlobalOutlined />}
                        />
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('fbAd.ctaLabel','Button Text')}</Text>
                        <Input
                          value={selected.cta_label || ''}
                          onChange={e => setSelected(p => ({ ...p, cta_label: e.target.value }))}
                          onBlur={e => handleUpdate('cta_label', e.target.value)}
                          placeholder={t('fbAd.learnMore','Learn More')}
                        />
                      </Col>
                    </Row>
                  </Space>
                </div>
              )}

              {/* ── AI CHAT MODE ── */}
              {mode === 'ai' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Messages */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}>
                    {(!selected.messages || selected.messages.length === 0) && (
                      <div style={{ paddingBottom: 16 }}>
                        <Alert
                          message={t('fbAd.aiModeTitle','AI Ad Generation Mode')}
                          description={t('fbAd.aiModeDesc','Describe your product, target audience and ad goal. AI will generate the full ad content.')}
                          type="info" showIcon style={{ marginBottom: 12 }}
                        />
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{t('fbAd.quickPrompts','Quick prompts:')}</Text>
                        {QUICK_PROMPTS.map((p, i) => (
                          <div
                            key={i}
                            onClick={() => setChatInput(p)}
                            style={{
                              padding: '8px 12px', marginBottom: 6, borderRadius: 8,
                              background: '#f6f0ff', border: '1px solid #d3adf7',
                              cursor: 'pointer', fontSize: 13, color: '#531dab',
                              transition: 'background .15s',
                            }}
                          >
                            → {p}
                          </div>
                        ))}
                      </div>
                    )}
                    {(selected.messages || []).map((m, i) => (
                      <div key={i} style={{
                        marginBottom: 10, display: 'flex',
                        flexDirection: m.role === 'user' ? 'row-reverse' : 'row', gap: 8,
                      }}>
                        <Avatar size={28} style={{ background: m.role === 'user' ? '#1890ff' : '#722ed1', flexShrink: 0 }}>
                          {m.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                        </Avatar>
                        <div style={{
                          maxWidth: '80%', padding: '8px 12px', borderRadius: 10, fontSize: 13,
                          background: m.role === 'user' ? '#1890ff' : '#f5f5f5',
                          color: m.role === 'user' ? '#fff' : '#333',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {m.content.replace(/>>>AD_READY[\s\S]*?AD_READY<<</g, `✅ ${t('fbAd.adGenerated','Ad generated — see preview on the right')}`)}
                        </div>
                      </div>
                    ))}
                    {sending && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        <Avatar size={28} style={{ background: '#722ed1' }}><RobotOutlined /></Avatar>
                        <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 10 }}><Spin size="small" /></div>
                      </div>
                    )}
                    <div ref={msgEndRef} />
                  </div>
                  {/* Chat input */}
                  <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <TextArea
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                        placeholder={t('fbAd.chatPlaceholder','Describe your ad requirements... (Enter to send)')}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{ fontSize: 13 }}
                      />
                      <Button type="primary" style={{ background: '#722ed1', borderColor: '#722ed1' }}
                        icon={<SendOutlined />} onClick={handleChat} loading={sending}
                        disabled={!chatInput.trim()} style={{ height: 'auto' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Publish bar */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Button block icon={<CopyOutlined />} onClick={() => {
                      const txt = `${selected.title || ''}\n\n${selected.body || ''}\n\n${selected.cta_url || ''}`;
                      navigator.clipboard.writeText(txt);
                      message.success(t('fbAd.copied','Ad content copied'));
                    }}>{t('fbAd.copyCopy','Copy')}</Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block type="primary"
                      icon={<FacebookOutlined />}
                      style={{ background: '#1877f2', borderColor: '#1877f2' }}
                      onClick={handlePublish}
                      loading={publishing}
                      disabled={!selected.title && !selected.body}
                    >
                      {selected.status === 'published' ? t('fbAd.published','Published') : t('fbAd.publishFb','Publish to Facebook')}
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>

            {/* ── Preview Panel (right) ── */}
            <div style={{ flex: 1, overflow: 'auto', padding: 24, background: '#f0f2f5' }}>
              <div style={{ maxWidth: 540, margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Title level={5} style={{ margin: 0 }}>{t('fbAd.livePreview','Live Preview')}</Title>
                  <Tag color="blue" icon={<EyeOutlined />}>{t('fbAd.fbAdFormat','Facebook Ad Format')}</Tag>
                </div>

                <FbAdPreview
                  ad={{ ...selected, image_url: imgSrc }}
                  onEdit={(field) => {
                    setMode('manual');
                    setEditField(field);
                  }}
                />

                {/* Image upload shortcut below preview */}
                <div style={{ marginTop: 16 }}>
                  <Upload.Dragger
                    beforeUpload={() => false}
                    customRequest={handleImageUpload}
                    showUploadList={false}
                    accept="image/*"
                    style={{ borderRadius: 8 }}
                  >
                    <Space>
                      <UploadOutlined />
                      <Text type="secondary" style={{ fontSize: 13 }}>{t('fbAd.dragDropImage','Drag & drop image here (1200×628px)')}</Text>
                    </Space>
                  </Upload.Dragger>
                </div>

                {/* Specs */}
                <Card size="small" style={{ marginTop: 12, background: '#fff3cd', border: '1px solid #ffe58f' }}>
                  <Text style={{ fontSize: 12 }}>
                    📐 <strong>{t('fbAd.metaSpec','Meta Image Specs:')}</strong> 1200×628px (16:9) or 1080×1080px (1:1) · JPG/PNG · ≤30MB · &lt;20% text
                  </Text>
                </Card>
              </div>
            </div>
          </div>
        )}
      </Content>
    </Layout>
  );
}
